import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { Box, TextField } from "@mui/material";
import { Dispatch, useCallback, useEffect, useMemo } from "react";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Text from "../../../../../components/Text";
import WithInfo from "../../../../../components/WithInfo";
import { patientBalance } from "../../../../../constants/provider/super-bill/add-super-bill/add-super-bill";
import {
  applyDefaultColumnSettings,
  toTwoDecimals,
} from "../../../../../utils/utils";
import CardView from "../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";

interface SuperBillCalculationProps {
  viewBill?: boolean;
  total?: number;
  calculationData: patientBalance;
  setCalculationData: Dispatch<React.SetStateAction<patientBalance>>;
  setTotal?: (total: string) => void;
  isSelfPay?: boolean;
  isPatientResponsibilityInvoice?: boolean;
}

function SuperBillCalculation({
  // `total` no longer rendered here (Procedure Charges label removed - ECRH-5371)
  calculationData,
  setCalculationData,
  setTotal,
  isPatientResponsibilityInvoice = false,
}: SuperBillCalculationProps) {
  // Memoized calculation function for total balance
  const calculateTotalBalance = useCallback(
    (data: patientBalance): number => {
      const subTotal = parseFloat(data.subTotal || "0") || 0;
      const advancedPaid = parseFloat(data.advancedPaid || "0") || 0;
      const insuranceAmount = parseFloat(data.insuranceAmount || "0") || 0;

      // For Patient Responsibility invoices, Subtotal already equals the
      // patient responsibility (Copay + Coinsurance + Deductible from the
      // posted ERA), so the insurance-paid amount is informational only and
      // Total Balance = PR - Adv Paid.
      // Total Balance should never display negative; clamp to zero.
      if (isPatientResponsibilityInvoice) {
        return Math.max(subTotal - advancedPaid, 0);
      }

      // Formula: total balance = sub-total - advanced paid - insurance amount
      return Math.max(subTotal - advancedPaid - insuranceAmount, 0);
    },
    [isPatientResponsibilityInvoice]
  );

  // Memoized total balance calculation
  const memoizedTotalBalance = useMemo(() => {
    const calBalance = calculateTotalBalance(calculationData);
    if (setTotal) {
      setTotal(calBalance?.toString() || "0");
    }
    return calBalance;
  }, [calculationData, calculateTotalBalance]);

  // Keep the persisted Total Balance in sync with the auto-calculated value
  // for Patient Responsibility invoices, so a clean load + Save stores the
  // computed PR balance even if the user never edits a field. (ECRH-5039)
  // Gated to PR invoices to leave the standard superbill flow untouched.
  useEffect(() => {
    if (!isPatientResponsibilityInvoice) return;
    const computed = memoizedTotalBalance.toString();
    setCalculationData((prev) =>
      (prev.total ?? "") === computed ? prev : { ...prev, total: computed }
    );
  }, [
    memoizedTotalBalance,
    isPatientResponsibilityInvoice,
    setCalculationData,
  ]);

  // Handler for all calculation fields
  const handleCalculationChange = useCallback(
    (fieldName: keyof patientBalance, value: string) => {
      setCalculationData((prev) => {
        const updatedData = {
          ...prev,
          [fieldName]: value,
        };

        // Auto-calculate total balance when any field changes
        const newTotalBalance = calculateTotalBalance(updatedData);

        return {
          ...updatedData,
          total: newTotalBalance.toString(),
        };
      });
    },
    [setCalculationData, calculateTotalBalance]
  );

  const patientBalanceData = [
    {
      id: 1,
      ...calculationData,
      total: memoizedTotalBalance.toString(), // Use calculated total
    },
  ];

  const patientBalanceColumns = applyDefaultColumnSettings<
    (typeof patientBalanceData)[0]
  >([
    {
      field: "subTotal",
      flex: 1,
      headerName: "Subtotal($)",
      width: 150,
      disableColumnMenu: true,
      headerClassName: "header-theme",
      renderCell: ({ value }) => (
        <TextField
          size="small"
          disabled
          name="subTotal"
          value={value ? toTwoDecimals(value) : ""}
        />
      ),
    },
    {
      field: "advancedPaid",
      flex: 1,
      headerName: "Adv Paid($)",
      width: 150,
      disableColumnMenu: true,
      headerClassName: "header-theme",
      renderCell: ({ value }) => (
        <TextField
          size="small"
          disabled
          name="advancedPaid"
          value={value || ""}
          onChange={(e) =>
            handleCalculationChange("advancedPaid", e.target.value)
          }
        />
      ),
    },
    // eCarehealth Production -10th july Tracker - Remove below fields
    //   Previous balance
    //   Ins amt
    {
      field: "insuranceAmount",
      flex: 1.3,
      headerName: "Insurance Amount to be submit(-$)",
      width: 250,
      disableColumnMenu: true,
      headerClassName: "header-theme",
      renderCell: ({ value }) => (
        <TextField
          size="small"
          name="insuranceAmount"
          value={value || ""}
          onChange={(e) =>
            handleCalculationChange("insuranceAmount", e.target.value)
          }
        />
      ),
    },
    {
      field: "previousBalance",
      flex: 1,
      headerName: "Prev. Balance($)",
      width: 150,
      disableColumnMenu: true,
      headerClassName: "header-theme",
      renderHeader: () => (
        <WithInfo title="Total unpaid amount from this patient's previous visits. See the breakdown under 'Payment History'">
          <Text fontWeight={"medium"} fontSize={"13px"}>
            Prev. Balance($)
          </Text>
        </WithInfo>
      ),
      renderCell: ({ value }) => (
        <TextField
          size="small"
          name="previousBalance"
          value={value || ""}
          onChange={(e) =>
            handleCalculationChange("previousBalance", e.target.value)
          }
        />
      ),
    },
    {
      field: "total",
      flex: 1,
      headerName: "Total Balance($)",
      width: 150,
      disableColumnMenu: true,
      headerClassName: "header-theme",
      renderCell: ({ value }) => (
        <TextField
          size="small"
          name="total"
          value={Number(value).toFixed(2) || ""}
          disabled
        />
      ),
    },
  ]);

  return (
    <Box id={"Super Bill Calculation"}>
      {/* Procedure Total */}
      <CardView
        title="Patient Balance"
        titleIcon={
          <AccountBalanceOutlinedIcon
            sx={{ color: "brown" }}
            fontSize="small"
          />
        }
      >
        {/* Patient Balance Table */}
        <Box sx={{ height: "140px" }}>
          <CustomDataGridV2
            rows={patientBalanceData ?? []}
            columns={patientBalanceColumns.filter(
              // Insurance Amount and Prev. Balance hidden per billing requirement
              (col) =>
                col.field !== "insuranceAmount" &&
                col.field !== "previousBalance"
            )}
            hideFooter
            disableRowSelectionOnClick
            rowHeight={80}
            columnHeaderHeight={45}
            sx={{
              "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
                px: 1.5,
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
                overflow: "visible",
              },
              "& .MuiDataGrid-columnHeaders": { mb: 1 },
              "& .MuiOutlinedInput-root": { fontSize: 14, height: 32 },
              "& .MuiOutlinedInput-input": { padding: "4px 8px" },
              "& .MuiOutlinedInput-root.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>

        {/* Procedure Charges label removed per ECRH-5371 */}
        {/* <Box borderBottom="1px solid #ddd" mb={1} />
        <Text fontWeight="bold">
          Procedure Charges &nbsp; ${total?.toFixed(2) ?? 0}
        </Text> */}
      </CardView>
    </Box>
  );
}

export default SuperBillCalculation;
